import React from 'react'
// libs
import moment from 'moment'
import { useSelector } from 'react-redux'
// components
import Slider from 'components/FormElements/Slider'
import { AvatarsList, HoverablePopover } from 'components/UI'
// icons
import WatchLaterIcon from '@mui/icons-material/WatchLaterOutlined'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
// constants
import { CURRENCIES } from 'constants/currencies'
import { PLAN_COLUMN_IDS } from 'constants/planColumns'
import { ENTITIES, OBJECT_ENTITY_NAMES } from 'constants/index'
// store
import { objectEntitiesSelector } from 'store/reducers/objectEntities'
// themes
import { colors } from 'themes'
import { LinkIcon } from 'themes/views/objectivesList'
import { Chip, ICON_STYLE, ReviewIcon } from 'themes/views/tables'
// types
import { IObjectEntity } from 'types/graphql'
import { IRowProps, ITableTreeNode } from 'components/UI/TableTree/types'
// views
import {
  ActionIcon,
  ActionsCell,
  CellContent,
  CollapseIcon,
  ColorBar,
  DataCell,
  DescriptionCell,
  DescriptionContent,
  DescriptionInnerContent,
  DragHandle,
  DropLine,
  FormCell,
  FormIndent,
  FormRow,
  FormStickyWrapper,
  LinkIconWithCounter,
  MeasureIcon,
  ObjectiveIcon,
  PathText,
  RowTr,
  TextWrapper,
  Title,
  TitlePopoverContent
} from 'components/UI/TableTree/views'
import { AvatarWrapper, ChipsWrapper, ProgressValue, ProgressWrapper } from './views'
// utils
import { getEntityStatus } from 'utils/fields'
import { getNodeEvents, isHelperNode } from 'components/UI/TableTree/utils'

// Leading entity-type icon per node type (objective / action / KPI).
const renderEntityIcon = (node: ITableTreeNode): React.ReactNode => {
  const iconColor = node.archived ? colors.gray220 : node.color

  if (node.entityType === ENTITIES.PLAN_ACTION) {
    return <ActionIcon $color={iconColor} />
  }
  if (node.entityType === ENTITIES.PLAN_KPI) {
    return <MeasureIcon $color={iconColor} />
  }
  if (node.entityType === ENTITIES.PLAN_OBJECTIVE) {
    return <ObjectiveIcon $color={iconColor} />
  }
  return null
}

const renderDue = (node: ITableTreeNode): React.ReactNode => {
  const { period, action } = getNodeEvents(node)

  if (!period && !action) {
    return null
  }

  return (
    <ChipsWrapper>
      {period && (
        <Chip
          rounded
          label={period.chipLabel}
          chipColor={period.chipColor}
          icon={<WatchLaterIcon style={ICON_STYLE} />}
        />
      )}
      {action && (
        <Chip
          rounded
          label={action.chipLabel}
          chipColor={action.chipColor}
          icon={<WatchLaterIcon style={ICON_STYLE} />}
        />
      )}
    </ChipsWrapper>
  )
}

const renderReview = (node: ITableTreeNode): React.ReactNode => {
  const { review } = getNodeEvents(node)

  if (!review) {
    return null
  }

  return (
    <ChipsWrapper>
      <Chip rounded icon={<ReviewIcon />} label={review.chipLabel} chipColor={review.chipColor} />
    </ChipsWrapper>
  )
}

const renderNextReview = (node: ITableTreeNode): React.ReactNode => {
  const { nextReview } = getNodeEvents(node)

  if (!nextReview) {
    return null
  }

  return (
    <ChipsWrapper>
      <Chip rounded icon={<ReviewIcon />} label={nextReview.chipLabel} chipColor={nextReview.chipColor} />
    </ChipsWrapper>
  )
}

const renderAssignee = (node: ITableTreeNode): React.ReactNode => {
  const { assignedPersons } = getNodeEvents(node)

  if (!assignedPersons.length) {
    return null
  }

  return (
    <AvatarWrapper>
      <AvatarsList list={assignedPersons} limit={3} />
    </AvatarWrapper>
  )
}

// Object entity holding the `status` picklist definition, per plan item type.
const OBJECT_ENTITY_NAME_BY_TYPE = {
  [ENTITIES.PLAN_OBJECTIVE]: OBJECT_ENTITY_NAMES.OBJECTIVE,
  [ENTITIES.PLAN_ACTION]: OBJECT_ENTITY_NAMES.ACTION,
  [ENTITIES.PLAN_KPI]: OBJECT_ENTITY_NAMES.KPI
}

// Actions and indicators have a fixed type. An objective is named after the type option picked
// for it, falling back to its plan type level when none is selected.
const renderType = (node: ITableTreeNode): React.ReactNode => {
  if (node.entityType === ENTITIES.PLAN_ACTION) {
    return OBJECT_ENTITY_NAMES.ACTION
  }
  if (node.entityType === ENTITIES.PLAN_KPI) {
    return OBJECT_ENTITY_NAMES.KPI
  }
  if (node.entityType === ENTITIES.PLAN_OBJECTIVE) {
    const typeOption = (node.level?.typeOptions || []).find((option) => option?.id === node.entity?.typeOptionId)

    return typeOption?.label || node.level?.description || OBJECT_ENTITY_NAMES.OBJECTIVE
  }
  return null
}

// The status picklist value carried by the entity — its label lives in the object's field settings.
const renderStatus = (node: ITableTreeNode, { objectEntities }: ICellContext): React.ReactNode => {
  const fields = objectEntities.find(({ name }) => name === OBJECT_ENTITY_NAME_BY_TYPE[node.entityType])?.fields

  if (!fields) {
    return null
  }

  return getEntityStatus(node.entity?.fieldValueMap, fields) || null
}

const renderDateChip = (date: string | undefined, chipColor: string): React.ReactNode =>
  date ? (
    <ChipsWrapper>
      <Chip
        rounded
        label={moment(date).format('D MMM YY')}
        chipColor={chipColor}
        icon={<WatchLaterIcon style={ICON_STYLE} />}
      />
    </ChipsWrapper>
  ) : null

// A 0 is a real value, so only a missing percentage leaves the cell empty. The slider is display
// only — the percentage is edited in the item's own modal.
const renderCompletionPercentage = (node: ITableTreeNode): React.ReactNode => {
  const percentage = node.entity?.completionPercentage

  if (percentage === null || percentage === undefined) {
    return null
  }

  const value = Math.round(percentage)

  return (
    <ProgressWrapper>
      <Slider size={'small'} value={value} min={0} max={100} tabIndex={-1} />
      <ProgressValue>{value}%</ProgressValue>
    </ProgressWrapper>
  )
}

const numberFormatter = new Intl.NumberFormat('en-AU')

// Resource totals are rolled up over the subtree in `applyTreeResourceTotals` (the backend only
// returns each plan item's own totals). A 0 is a real value, so only a missing total leaves the
// cell empty.
const renderResourcesTotal = (total: number | undefined, currencySymbol: string): React.ReactNode =>
  total === null || total === undefined ? null : `${currencySymbol} ${numberFormatter.format(total)}`

interface ICellContext {
  objectEntities: IObjectEntity[]
  currencySymbol: string
}

// Body cell per configured column (see constants/planColumns.ts).
const CELL_RENDERERS: Record<string, (node: ITableTreeNode, context: ICellContext) => React.ReactNode> = {
  [PLAN_COLUMN_IDS.TYPE]: renderType,
  [PLAN_COLUMN_IDS.STATUS]: renderStatus,
  [PLAN_COLUMN_IDS.DUE]: renderDue,
  [PLAN_COLUMN_IDS.COMMENCEMENT_AT]: (node) => renderDateChip(node.entity?.commencementAt, colors.gray350),
  [PLAN_COLUMN_IDS.ACTUAL_COMPLETION_DATE]: (node) =>
    renderDateChip(node.entity?.actualCompletionDate, colors.green300),
  [PLAN_COLUMN_IDS.COMPLETION_PERCENTAGE]: renderCompletionPercentage,
  [PLAN_COLUMN_IDS.REVIEW]: renderReview,
  [PLAN_COLUMN_IDS.NEXT_REVIEW]: renderNextReview,
  [PLAN_COLUMN_IDS.ASSIGNEE]: renderAssignee,
  [PLAN_COLUMN_IDS.TOTAL_RESOURCE_BUDGET]: (node, { currencySymbol }) =>
    renderResourcesTotal(node.resourceBudget, currencySymbol),
  [PLAN_COLUMN_IDS.TOTAL_RESOURCE_COST]: (node, { currencySymbol }) =>
    renderResourcesTotal(node.resourceCost, currencySymbol)
}

// The full title is only worth a popover when the cell actually cut it off — measured on the
// hovered element itself, so it stays correct as the column is resized.
const isTextTruncated = (event: React.MouseEvent): boolean => {
  const element = event.currentTarget as HTMLElement

  return element.scrollWidth > element.clientWidth
}

// Plan-specific row for the plan items table.
const PlanItemRow: React.FC<IRowProps> = ({
  dnd,
  node,
  depth,
  columns = [],
  expanded,
  formWidth,
  onRowClick,
  isRowDimmed,
  toggleExpand,
  getNodeTitle,
  forceExpandAll,
  RowComponent,
  renderRowActions,
  renderCreateForm
}) => {
  const objectEntities = useSelector(objectEntitiesSelector)

  const { company } = useCurrentUser()

  // Falls back to USD for an unset or unknown company currency.
  const currencySymbol = (CURRENCIES[company?.currency] || CURRENCIES.USD).symbol

  // Helper/create-form nodes render an indented full-width form row (or nothing when unsupported/empty).
  if (isHelperNode(node)) {
    const formContent = renderCreateForm?.(node)

    if (!formContent) {
      return null
    }

    return (
      <FormRow>
        <FormCell colSpan={1 + columns.length + (renderRowActions ? 1 : 0)}>
          <FormStickyWrapper style={formWidth ? { width: formWidth } : undefined}>
            <FormIndent $depth={depth}>{formContent}</FormIndent>
          </FormStickyWrapper>
        </FormCell>
      </FormRow>
    )
  }

  const children = node.children || []
  const hasChildren = children.length > 0
  const isOpen = forceExpandAll || expanded.has(node.id)

  const linkedCount =
    (node.linkingItems?.destinationLinkingItems?.length || 0) + (node.linkingItems?.sourceLinkingItems?.length || 0)

  const isDraggable = !!dnd && dnd.canDrag(node)
  const isDropTarget = dnd?.dragOver.id === node.id
  const dropPosition = isDropTarget ? dnd?.dragOver.position : null

  const handleCollapseClick = (event: React.MouseEvent) => {
    event.stopPropagation()

    if (hasChildren) {
      toggleExpand(node)
    }
  }

  // All drag & drop handlers together — attached only when DnD is enabled.
  const dndProps = dnd
    ? {
        draggable: isDraggable,
        onDragStart: (event: React.DragEvent) => dnd.onDragStart(event, node),
        onDragOver: (event: React.DragEvent) => dnd.onDragOver(event, node),
        onDragLeave: dnd.onDragLeave,
        onDrop: (event: React.DragEvent) => dnd.onDrop(event, node),
        onDragEnd: dnd.onDragEnd
      }
    : {}

  const isRoot = depth === 0 && !node.archived

  return (
    <>
      <RowTr
        $isRoot={isRoot}
        $color={node.color}
        $clickable={!!onRowClick}
        $isDragging={dnd?.draggedId === node.id}
        $dimmed={isRowDimmed?.(node)}
        data-testid='plan-table-row'
        data-entity-id={node.entityId}
        onClick={() => onRowClick?.(node)}
        {...dndProps}>
        <DescriptionCell>
          {dropPosition && dnd && <DropLine $depth={dnd.dragOver.depth} $position={dropPosition} />}
          <DescriptionContent $depth={depth}>
            {isDraggable && <DragHandle />}
            <DescriptionInnerContent>
              <ColorBar $color={node.color} $archived={!!node.archived} />
              {hasChildren && <CollapseIcon $expanded={isOpen} onClick={handleCollapseClick} />}
              {renderEntityIcon(node)}
              <TextWrapper>
                <HoverablePopover shouldOpen={isTextTruncated} trigger={<Title>{getNodeTitle(node)}</Title>}>
                  <TitlePopoverContent>{getNodeTitle(node)}</TitlePopoverContent>
                </HoverablePopover>
                {node.referenced && node.path && <PathText>{node.path}</PathText>}
              </TextWrapper>
              {linkedCount > 0 && (
                <LinkIconWithCounter>
                  <div>{linkedCount}</div>
                  <LinkIcon $isLinked />
                </LinkIconWithCounter>
              )}
            </DescriptionInnerContent>
          </DescriptionContent>
        </DescriptionCell>
        {columns.map((column) => (
          <DataCell key={column.key} data-testid='plan-table-cell' data-column-id={column.key}>
            <CellContent $width={column.width}>
              {CELL_RENDERERS[column.key]?.(node, { objectEntities, currencySymbol })}
            </CellContent>
          </DataCell>
        ))}
        {renderRowActions && (
          <ActionsCell onClick={(event) => event.stopPropagation()}>
            <div>{renderRowActions(node)}</div>
          </ActionsCell>
        )}
      </RowTr>
      {hasChildren &&
        isOpen &&
        children.map((child) => (
          <RowComponent
            dnd={dnd}
            key={child.id}
            node={child}
            depth={depth + 1}
            columns={columns}
            expanded={expanded}
            formWidth={formWidth}
            onRowClick={onRowClick}
            isRowDimmed={isRowDimmed}
            toggleExpand={toggleExpand}
            getNodeTitle={getNodeTitle}
            forceExpandAll={forceExpandAll}
            RowComponent={RowComponent}
            renderRowActions={renderRowActions}
            renderCreateForm={renderCreateForm}
          />
        ))}
    </>
  )
}

export default PlanItemRow
